import React, { createContext, useCallback, useContext, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import { Variant } from "react-bootstrap/esm/types";

export type Notification = {
  id: string;
  header: React.ReactNode;
  body: React.ReactNode;
  variant?: Variant;
};

const NotificationComp = ({
  header,
  body,
  variant,
  onClosed,
}: Omit<Notification, "id"> & { onClosed: () => void }) => {
  const [show, setShow] = useState(true);
  const onClose = useCallback(() => {
    setShow(false);
  }, []);

  return (
    <Toast
      onClose={onClose}
      onExited={onClosed}
      show={show}
      bg={`${variant?.toLowerCase()}-subtle`}
      // A failure stays until the advisor closes it with the close button of
      // the header: its body can be a long sentence from the api. Every other
      // notification hides itself after the default delay of the Toast.
      autohide={variant !== "danger"}
    >
      <Toast.Header>
        <strong className="me-auto">{header}</strong>
      </Toast.Header>
      <Toast.Body>{body}</Toast.Body>
    </Toast>
  );
};

// One React key for each pushed notification. `id` cannot be the key: call
// sites push one `id` again and again (a page uses one `id` for each of its
// successes and failures), and two notifications with one `id` can be on the
// screen at the same time.
const reactKeys = new WeakMap<Notification, number>();
let nextReactKey = 0;
const reactKey = (notification: Notification) => {
  let key = reactKeys.get(notification);
  if (key === undefined) {
    key = nextReactKey++;
    reactKeys.set(notification, key);
  }
  return key;
};

export const NotificationContainer = () => {
  const { notifications, setNotifications } = useContext(NotificationContext);

  return (
    <ToastContainer
      position="top-end"
      className="notification-container p-3 position-fixed"
      style={{ top: "65px !important" }}
    >
      {notifications.map((notification) => (
        <NotificationComp
          key={reactKey(notification)}
          header={notification.header}
          body={notification.body}
          variant={notification.variant}
          // A closed notification leaves the list, so the list holds only
          // what is on the screen.
          onClosed={() =>
            setNotifications((current) =>
              current.filter((other) => other !== notification),
            )
          }
        />
      ))}
    </ToastContainer>
  );
};

export const NotificationContext = createContext<{
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  pushNotification: (notification: Notification) => void;
}>({
  notifications: [],
  setNotifications: () => null,
  pushNotification: () => null,
});

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const context = {
    notifications,
    setNotifications,
    pushNotification: (notification: Notification) => {
      // A copy, so that each push is its own object and gets its own React
      // key, and from the current list, so that two pushes in one render both
      // stay.
      setNotifications((current) => [...current, { ...notification }]);
    },
  };

  return (
    <NotificationContext.Provider value={context}>
      {children}
    </NotificationContext.Provider>
  );
};
